import React, { Fragment, Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { I18nextProvider } from 'react-i18next';
import { Provider as BusProvider } from 'react-bus';

import get from 'lodash/get';

import { store, persistor } from './store';
import { useAppDispatch } from './store/hooks';
import { setCurrentPage } from './store/slices/uiSlice';
import { initializeAuthFromToken } from './store/slices/authSlice';
import authService from './services/authService';
import DynamicThemeWrapper from './components/common/DynamicThemeWrapper';
import i18n from './i18n';
import AnalyticsProvider from './analytics/AnalyticsProvider';
import LoadingSpinner from './components/common/LoadingSpinner';
import AIPlanLimitModal from './components/common/AIPlanLimitModal';
import SubscriptionWatcher from './components/common/SubscriptionWatcher';

// Routes
import LandingRoutes from './routes/LandingRoutes';
import AuthRoutes from './routes/AuthRoutes';
import StudentRoutes from './routes/StudentRoutes';
// Tiny auth gate on every protected page: a lazy chunk here only added a round trip.
import ProtectedRoute from './routes/ProtectedRoutes';
const AdminRoutes = React.lazy(() => import('./routes/AdminRoutes'));
const ForceChangePasswordPage = React.lazy(() => import('./features/auth/ForceChangePasswordPage'));
const AIConversationIframe = React.lazy(() => import('@/features/iframe/AIConversationIframe'));
import { AppEvent, emitter } from './plugin/mitt';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import 'dayjs/locale/vi';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { useNotification } from './hooks/useNotification';
import { INavigate, INotification } from './common/interface';
import { SEVERITY_NOTIFICATION } from './common/constants';
import { AudioPlayerProvider } from './contexts';

// URL Change Tracker Component
const URLChangeTracker: React.FC = () => {
    const location = useLocation();
    const dispatch = useAppDispatch();
    useEffect(() => {
        dispatch(setCurrentPage(location.pathname));
    }, [location.pathname, dispatch]);

    return null;
};

// Main App Content
const AppContent: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const notify = useNotification();
    const dispatch = useAppDispatch();

    useEffect(() => {
        const handler = (data: INavigate) => {
            if (typeof data.to === 'number') {
                navigate(data.to);
            } else {
                navigate(data.to, data.options);
            }
        };
        emitter.on(AppEvent.NAVIGATE, handler);
        return () => {
            emitter.off(AppEvent.NAVIGATE, handler);
        };
    }, [navigate]);

    useEffect(() => {
        const handler = (data: INotification) => {
            const { code, message, severity = SEVERITY_NOTIFICATION.INFO, duration } = data;
            notify({
                message: code ? t(code, message) : message,
                severity: severity,
                duration: duration,
            });
        };
        emitter.on(AppEvent.SHOW_NOTIFICATION, handler);
        return () => {
            emitter.off(AppEvent.SHOW_NOTIFICATION, handler);
        };
    }, [t]);

    // Initialize auth state on app startup
    useEffect(() => {
        // Signed-in state comes from /auth/user/ accepting the HttpOnly cookie session (EAA-2090)
        if (authService.hasSession()) {
            dispatch(initializeAuthFromToken());
        }
    }, [dispatch]);
    return (
        <Suspense fallback={<LoadingSpinner />}>
            <Fragment>
                <URLChangeTracker />
                <AIPlanLimitModal />
                <SubscriptionWatcher />
                <AnalyticsProvider>
                    <CssBaseline />
                    <Routes>
                        <Route path="/iframe/ai-tutor" element={<AIConversationIframe />} />
                        <Route
                            path="/change-password-required"
                            element={
                                <ProtectedRoute allowPasswordChange>
                                    <ForceChangePasswordPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/auth/*" element={<AuthRoutes />} />
                        <Route
                            path="/student/*"
                            element={
                                <ProtectedRoute>
                                    <StudentRoutes />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/*"
                            element={
                                <ProtectedRoute>
                                    <AdminRoutes />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/*" element={<LandingRoutes />} />
                    </Routes>
                </AnalyticsProvider>
            </Fragment>
        </Suspense>
    );
};

function App() {
    const { i18n: globalI18N } = useTranslation();

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={get(globalI18N, 'language', 'vi')}>
            <BusProvider>
                <Provider store={store}>
                    <PersistGate loading={<LoadingSpinner />} persistor={persistor}>
                        <I18nextProvider i18n={i18n}>
                            <AudioPlayerProvider>
                                <DynamicThemeWrapper>
                                    <Router>
                                        <AppContent />
                                    </Router>
                                </DynamicThemeWrapper>
                            </AudioPlayerProvider>
                        </I18nextProvider>
                    </PersistGate>
                </Provider>
            </BusProvider>
        </LocalizationProvider>
    );
}

export default App;
